import { useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, PartyPopper } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { UptimeWingLogo } from "@/components/shared/UptimeWingLogo";

interface WaitlistModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    initialPlan?: string;
}

export function WaitlistModal({ open, onOpenChange, initialPlan = "pro_founder" }: WaitlistModalProps) {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email.trim()) return;

        setLoading(true);
        try {
            const { data: { session } } = await supabase.auth.getSession();
            
            const res = await supabase.functions.invoke('handle-waitlist', {
                body: { email: email.trim(), plan_interest: initialPlan }
            });

            if (res.error) {
                if (res.error.context?.status === 409 || res.error.message?.includes('Already on the list')) {
                    toast.info("You're already on the list! We'll reach out soon.");
                    setSuccess(true);
                    return;
                }
                throw new Error(res.error.message || "Failed to join waitlist");
            }

            setSuccess(true);
            toast.success("Welcome to the Founders Pack!");
        } catch (err: any) {
            toast.error(err.message || "Something went wrong. Try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-background border-4 border-foreground shadow-brutal p-8 sm:max-w-md rounded-none overflow-hidden">
                <AnimatePresence mode="wait">
                    {!success ? (
                        <motion.div
                            key="form"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                        >
                            <DialogHeader className="mb-6">
                                <div className="h-14 w-14 bg-primary border-2 border-foreground flex items-center justify-center shadow-xs mb-4 p-2">
                                    <UptimeWingLogo className="h-full w-full" />
                                </div>
                                <DialogTitle className="text-3xl font-black uppercase tracking-tighter text-foreground">
                                    Join Founders Pack
                                </DialogTitle>
                                <DialogDescription className="text-xs font-bold uppercase tracking-widest text-muted-foreground mt-2">
                                    Lock in your founder price before launch. Get unlimited monitors,
                                    1-minute checks, and priority support — locked in forever.
                                </DialogDescription>
                            </DialogHeader>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase text-foreground">Your Email Address</label>
                                    <Input
                                        type="email"
                                        placeholder="founder@epic.io"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        className="h-12 rounded-none border-2 border-foreground font-mono shadow-xs focus:ring-0"
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    disabled={loading}
                                    className="h-14 w-full rounded-none border-2 border-foreground bg-primary text-primary-foreground font-black uppercase tracking-tighter shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all"
                                >
                                    {loading ? (
                                        <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                                    ) : (
                                        "Secure My Access"
                                    )}
                                </Button>
                            </form>
                        </motion.div>
                    ) : (
                        <motion.div
                            key="success"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="text-center py-6"
                        >
                            <div className="h-20 w-20 bg-[hsl(var(--status-up))] border-4 border-foreground flex items-center justify-center shadow-brutal mx-auto mb-6">
                                <PartyPopper className="h-10 w-10 text-white" />
                            </div>
                            <h3 className="text-2xl font-black uppercase text-foreground mb-2">You're In.</h3>
                            <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest leading-relaxed">
                                We've logged your interest. Watch your inbox—when the floodgates open, you'll be the first to know.
                            </p>
                            <Button
                                onClick={() => onOpenChange(false)}
                                className="mt-8 h-12 w-full rounded-none border-2 border-foreground bg-foreground text-background font-black uppercase tracking-tighter hover:bg-muted-foreground transition-all"
                            >
                                Back to Base
                            </Button>
                        </motion.div>
                    )}
                </AnimatePresence>
            </DialogContent>
        </Dialog>
    );
}
